import {Dialog} from '@material-ui/core';
import React from 'react';
import Timeago from 'react-timeago';
import {toast} from 'react-toastify';

import {useDeleteView2Mutation} from '../../generated/graphql';
import {ViewMetadata} from '../../graphql/views';
import * as Analytics from '../../services/analytics';
import * as ReportTypes from '../../state/reports/types';
import {useViewer} from '../../state/viewer/hooks';
import {LoadableView} from '../../state/views/types';
import * as S from './ReportDraftModals.styles';

export enum DiscardReportDraftAnalyticsOrigin {
  ReportTable = 'report table',
  ReportPage = 'report page',
}

type DiscardReportDraftModalProps = {
  isModalOpen: boolean;
  view?: ViewMetadata | LoadableView;
  draftId: string;
  analyticsOrigin: DiscardReportDraftAnalyticsOrigin;
  refetch?: () => void;
  onDiscardClick?: () => void;
  onModalClose: () => void;
};

export const DiscardReportDraftModal = ({
  isModalOpen,
  view,
  draftId,
  refetch,
  analyticsOrigin,
  onDiscardClick,
  onModalClose,
}: DiscardReportDraftModalProps) => {
  const viewer = useViewer();

  // Determine who last edited this report
  const updatedBy = view?.updatedBy;
  const isUpdatedByViewer = viewer?.id === updatedBy?.id;
  const lastUpdatedName = updatedBy?.name ?? updatedBy?.username;

  const [deleteViewMutation] = useDeleteView2Mutation({
    variables: {id: draftId, deleteDrafts: false},
  });

  const onClick = async () => {
    Analytics.reportDiscardDraftClicked({
      reportMode:
        view?.type === ReportTypes.REPORT_DRAFT_VIEW_TYPE ? 'edit' : 'view',
      location: analyticsOrigin,
    });

    try {
      await deleteViewMutation();
      await refetch?.();
      await onDiscardClick?.();
      toast('Draft sucessfully discarded.');
      onModalClose();
    } catch (e) {
      console.error(e);
      toast('Error occurred while discarding drafts. Please try again.');
    }
  };

  return (
    <Dialog open={isModalOpen} onClose={onModalClose}>
      <S.DialogTitle>Discard draft?</S.DialogTitle>
      <S.DialogContent>
        {view == null || lastUpdatedName == null ? (
          <p>Are you sure you want to discard this draft?</p>
        ) : (
          <p>
            {isUpdatedByViewer ? 'You' : lastUpdatedName} made edits to this
            report {<Timeago date={view?.updatedAt} live={false} />}. Are you
            sure you want to discard {isUpdatedByViewer ? 'your' : 'their'}{' '}
            changes?
          </p>
        )}
      </S.DialogContent>
      <S.DialogActions $align="left">
        <S.DestructiveButton variant="contained" onClick={onClick}>
          Yes, discard this draft
        </S.DestructiveButton>
        <S.GhostButton variant="text" onClick={onModalClose}>
          Cancel
        </S.GhostButton>
      </S.DialogActions>
    </Dialog>
  );
};
